// src/components/PrivacyConsentSection.jsx
// Settings → "Privacy & consent" tab. DPDP Act 2023 §6: the patient can see each
// consent they have given, withdraw it, and give it again.
//
// Withdrawing the core consent stops Previu processing the patient's health data
// (chat, triage, new uploads). Existing records are kept — deleting them is a
// separate request, not part of withdrawal. The copy below says so plainly, since
// a patient should know what the button does before pressing it.
//
// Uses GET/POST /api/patients/{id}/consents (consentAPI in services/api.js).

import React, { useCallback, useEffect, useState } from 'react';
import { consentAPI } from '../services/api';

import Icon from './Icon';
const PURPOSE_LABELS = {
  CORE_SERVICE: {
    title: 'Health data processing',
    desc: 'Lets Previu store your health records and use them to generate insights, ' +
      'triage results and care recommendations. Required to use Previu.',
  },
  DATA_SOURCE: {
    title: 'Lab partner data',
    desc: 'Lets Previu receive your results directly from partner laboratories.',
  },
  SHARING: {
    title: 'Sharing with your care team',
    desc: 'Lets Previu share your results with the clinicians you choose.',
  },
  RESEARCH: {
    title: 'Anonymised research',
    desc: 'Optional. Lets Previu include anonymised data in population health research.',
  },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const errorText = (e, fallback) => e?.response?.data?.message || fallback;

export default function PrivacyConsentSection({ patientId }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [notice, setNotice]     = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [reason, setReason]     = useState('');
  const [busy, setBusy]         = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError('');
    try {
      const res = await consentAPI.list(patientId);
      setOverview(res.data);
    } catch (e) {
      setError(errorText(e, 'Could not load your consent settings. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  function startWithdraw(id) {
    setConfirmingId(id);
    setReason('');
    setNotice('');
    setError('');
  }

  async function confirmWithdraw(id) {
    setBusy(true);
    setError('');
    try {
      await consentAPI.revoke(patientId, id, reason.trim());
      setConfirmingId(null);
      setNotice('Your consent has been withdrawn.');
      await load();
    } catch (e) {
      setError(errorText(e, 'Could not withdraw your consent. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function giveConsentAgain() {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await consentAPI.grant(patientId, 'CORE_SERVICE');
      setNotice('Thank you — your consent has been recorded.');
      await load();
    } catch (e) {
      setError(errorText(e, 'Could not record your consent. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !overview) {
    return <div className="skeleton skeleton--row" aria-label="Loading your consent settings" />;
  }

  const status   = overview?.coreConsentStatus;
  const consents = overview?.consents || [];

  return (
    <div className="card">
      <div className="card-title">Privacy &amp; consent</div>
      <p className="consent-note">
        Under the Digital Personal Data Protection Act 2023 you decide how Previu uses your
        health data. You can withdraw a consent at any time; withdrawing is as easy as giving it.
      </p>

      {error  && <div className="alert alert-error" role="alert"><Icon name="alert" size={14} /> {error}</div>}
      {notice && <div className="alert alert-success" role="status">{notice}</div>}

      {status === 'WITHDRAWN' && (
        <div className="alert alert-warning" role="status" style={{ display: 'block' }}>
          <strong>You have withdrawn your consent.</strong> Previu will not process your health
          data: the assistant, report analysis and new uploads are paused. Your existing records
          are kept. Give your consent again to resume.
          <div className="consent-confirm-actions">
            <button type="button" className="btn btn-primary btn-sm"
              onClick={giveConsentAgain} disabled={busy}>
              {busy ? 'Saving…' : 'Give consent again'}
            </button>
          </div>
        </div>
      )}

      {status === 'NOT_RECORDED' && (
        <div className="alert alert-info" role="status" style={{ display: 'block' }}>
          We have no consent recorded for your account yet. If you would like to withdraw
          consent or have a question about your data, contact your Previu navigator.
        </div>
      )}

      {consents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-title">No consents on record</div>
        </div>
      ) : (
        <div className="consent-list">
          {consents.map(c => {
            const label = PURPOSE_LABELS[c.purpose] || { title: c.purpose, desc: '' };
            const confirming = confirmingId === c.id;
            return (
              <div key={c.id} className={`consent-item${c.active ? '' : ' consent-item--withdrawn'}`}>
                <div className="consent-item-body">
                  <div className="consent-item-title">
                    {label.title}
                    <span className={`badge ${c.active ? 'badge-active' : 'badge-inactive'}`}>
                      {c.active ? 'Active' : 'Withdrawn'}
                    </span>
                  </div>
                  {label.desc && <div className="consent-item-meta">{label.desc}</div>}
                  <div className="consent-item-meta">
                    Given {formatDate(c.consentedAt)}
                    {c.revokedAt && ` · Withdrawn ${formatDate(c.revokedAt)}`}
                    {c.revocationReason && ` · “${c.revocationReason}”`}
                  </div>

                  {confirming && (
                    <div className="consent-confirm">
                      {c.purpose === 'CORE_SERVICE' ? (
                        <div className="consent-note">
                          If you withdraw this, Previu will stop processing your health data:
                          the assistant, report analysis and new uploads will be paused. Your
                          existing records are kept, and you can give consent again at any time.
                        </div>
                      ) : (
                        <div className="consent-note">
                          Previu will stop using your data for this purpose. Your other consents
                          are not affected.
                        </div>
                      )}
                      <label className="field-label" htmlFor={`consent-reason-${c.id}`}>
                        Reason (optional)
                      </label>
                      <input
                        id={`consent-reason-${c.id}`}
                        className="field-text"
                        type="text"
                        maxLength={500}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                      />
                      <div className="consent-confirm-actions">
                        <button type="button" className="btn btn-danger btn-sm"
                          onClick={() => confirmWithdraw(c.id)} disabled={busy}>
                          {busy ? 'Withdrawing…' : 'Withdraw consent'}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm"
                          onClick={() => setConfirmingId(null)} disabled={busy}>
                          Keep my consent
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {c.active && !confirming && (
                  <div className="consent-item-actions">
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => startWithdraw(c.id)} disabled={busy}>
                      Withdraw
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
